import { useContext, useState, type ReactNode } from 'react';
import { Button } from '@shared/ui/Button';
import { Muted, Row, Small, Table, TableBox } from '@widgets/ksp-workspace/parts';
import { CollectionContext, collectionNames } from '../types';

export function CollectionLoad({ collection }: { collection: string }) {
  const context = useContext(CollectionContext);
  const meta = context?.workspace.collections?.[collection];
  if (!meta) return null;
  return (
    <Row>
      <Small>
        {collectionNames[collection] ?? collection}: загружено {meta.offset + meta.returned} из{' '}
        {meta.total}
      </Small>
      {meta.truncated && (
        <Button
          $variant="ghost"
          disabled={!!context?.loading}
          onClick={() => context?.load(collection)}
        >
          {context?.loading === collection ? 'Загрузка…' : 'Подгрузить ещё'}
        </Button>
      )}
    </Row>
  );
}

export function PagedTable<T>({
  rows,
  columns,
  collection: collectionKey,
}: {
  rows: T[];
  columns: { name: string; value: (row: T) => ReactNode }[];
  collection?: string;
}) {
  const context = useContext(CollectionContext);
  const collection =
    collectionKey ??
    Object.keys(context?.workspace.collections ?? {}).find(
      (key) => (context?.workspace as unknown as Record<string, unknown>)?.[key] === rows,
    );
  const [page, setPage] = useState(0);
  const effective = Math.min(page, Math.max(0, Math.ceil(rows.length / 50) - 1));
  return (
    <>
      <TableBox>
        <Table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.name}>{c.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(effective * 50, effective * 50 + 50).map((row, i) => (
              <tr key={(row as { id?: string }).id ?? `${effective}-${i}`}>
                {columns.map((c) => (
                  <td key={c.name}>{c.value(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableBox>
      {rows.length === 0 && <Muted>Записей пока нет.</Muted>}
      {collection && <CollectionLoad collection={collection} />}
      {rows.length > 50 && (
        <Row>
          <Button
            $variant="ghost"
            disabled={effective === 0}
            onClick={() => setPage(effective - 1)}
          >
            Назад
          </Button>
          <Small>
            {effective + 1} / {Math.ceil(rows.length / 50)} · всего {rows.length}
          </Small>
          <Button
            $variant="ghost"
            disabled={(effective + 1) * 50 >= rows.length}
            onClick={() => setPage(effective + 1)}
          >
            Далее
          </Button>
        </Row>
      )}
    </>
  );
}
